import { useState, useMemo, useCallback } from 'react'
import SearchBar from '../../../baseComponents/SearchBar'
import { TwinAutoSizer, TwinList } from '../../../baseComponents/TwinTable/Adapters'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { ChangeValueFunc } from '../../../utils/globals/types'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { InputCheckbox } from '../../Checkbox'
import { filterCustomSelectItems, sortItems } from '../DropDown/functions'
import { DropDownComponentType, ItemCustomSelectDropDownRow, CustomSelectDropDownBaseProps, CustomSelectDropDownProps } from '../DropDown/types'
import { includesByArrayType } from '../../../utils/globals/array'

export const DropDownWithSearchBarAndCheckbox: DropDownComponentType = ({ ...props }) => {
    const { t } = useTwinTranslation()
    const Component = DefaultDropDownCheckboxDecider({ sortItems: props.sortItems, sortFunction: props.sortFunction })
    const { searchValue, changeSearchValue, filteredItems, isAllSelected, parsedFilteredItems } = useDropDownWithSearchBarAndCheckboxLogic({ items: props.items, extraParams: props.extraParams || {}, fieldName: props.fieldName, fieldValue: 'id'})
    const selectAllText = t('selectAll', 'Seleccionar todos')
    return (
        <Component {...props} items={filteredItems} selectedValues={props?.extraParams?.selectedValues || []} onChange={props.extraParams?.onChange}>
            <SearchBar className='custom_select_search_bar c_s_search_bar_checkbox' placeholder={t('search', 'Buscar')} onChange={changeSearchValue} {...{ searchValue }} />
            {parsedFilteredItems.length ?
                <div className='custom_select_value_checkbox c_s_checkbox_select_all cursor-pointer flex items-center bg-white regular12 px-10 gap-x-10 h-35' onClick={(e) => props?.extraParams?.markAllAsSelected?.(e, parsedFilteredItems, isAllSelected)}>
                    <InputCheckbox checked={isAllSelected} className='ml-5' />
                    <span className='block twin_elipsis'>{selectAllText}</span>
                </div>
                : null
            }
        </Component>
    )
}

interface DropDownWithSearchBarAndCheckboxLogicProps {
    items: TwinDictionary,
    extraParams: TwinDictionary
    fieldName?: string,
    fieldValue: string,
}
export const useDropDownWithSearchBarAndCheckboxLogic = ({ items, extraParams, fieldName, fieldValue = 'id' }: DropDownWithSearchBarAndCheckboxLogicProps  ) => {
    const [searchValue, setSearchValue] = useState<string>('')
    const filteredItems = useMemo(() => filterCustomSelectItems(searchValue, items, fieldName), [searchValue, items, fieldName])
    const parsedFilteredItems = Object.values(filteredItems)
    const selectedValues = extraParams?.selectedValues || []
    let isAllSelected = true
    for (const item of parsedFilteredItems) {
        if (!includesByArrayType(selectedValues, String(item[fieldValue]))) {
            isAllSelected = false
        }
    }

    const changeSearchValue: ChangeValueFunc = useCallback((myvalue) => {
        setSearchValue(myvalue)
    }, [setSearchValue])

    return { searchValue, changeSearchValue, filteredItems, isAllSelected, parsedFilteredItems }
}

interface DefaultDropDownCheckboxDeciderProps {
    sortItems?: boolean
    children?: React.ReactNode
    sortFunction?: (items: TwinDictionary) => ItemCustomSelectDropDownRow[]
}

export const DefaultDropDownCheckboxDecider = ({ sortItems, sortFunction }: DefaultDropDownCheckboxDeciderProps) => {
    if (sortFunction || sortItems) {
        return CustomSelectDropDownCheckboxSorted
    }
    return CustomSelectDropDownCheckboxUnsorted
}

interface CustomSelectDropDownSortedProps extends CustomSelectDropDownBaseProps {
    selectedValues?: any[]
}

export const CustomSelectDropDownCheckboxSorted: React.FC<CustomSelectDropDownSortedProps> = ({ items, children, fieldName, sortFunction, selectedValues, ...rest }) => {
    const itemsSorted = useMemo(() => sortFunction ? sortFunction(items, fieldName) : sortItems(items, fieldName), [items, fieldName, sortFunction])

    return (<CustomSelectDropDownCheckbox items={itemsSorted} {...rest} rowRenderer={({ ...rowRenderProps }) => {
        return (
            <CustomSelectDropDownCheckboxRow items={itemsSorted} onChange={rest.onChange} selectedValues={selectedValues || []} item={{ value: itemsSorted[rowRenderProps.index].value, text: itemsSorted[rowRenderProps.index].text }} {...rowRenderProps} key={itemsSorted[rowRenderProps.index].value}/>
        )
    }}>{children}</CustomSelectDropDownCheckbox>)
}

export const CustomSelectDropDownCheckboxUnsorted: React.FC<CustomSelectDropDownSortedProps> = ({ items, children, fieldName = 'name', selectedValues, ...rest }) => {
    const keys = useMemo(() => Object.keys(items), [items])

    return (<CustomSelectDropDownCheckbox items={keys} {...rest} rowRenderer={({ ...rowRenderProps }) => {
        return (
            <CustomSelectDropDownCheckboxRow items={keys} onChange={rest.onChange} selectedValues={selectedValues || []} item={{ value: keys[rowRenderProps.index], text: items[keys[rowRenderProps.index]]?.[fieldName] }}  {...rowRenderProps} key={'checkbox_row' + items[keys[rowRenderProps.index]]?.id} />
        )
    }}>{children}</CustomSelectDropDownCheckbox>)
}

const ROW_HEIGHT = 35

export const CustomSelectDropDownCheckbox: React.FC<CustomSelectDropDownProps> = ({ items, children, rowRenderer, maxItems = 8 }) => {
    return (
        <div className='custom_select_dropdown c_s_dropdown_checkbox'>
            {children}
            <TwinAutoSizer>
                {({ width }) => (
                    <TwinList className='bg-white shadow z-1'
                        height={items.length > maxItems ? ROW_HEIGHT * maxItems : ROW_HEIGHT * items.length}
                        rowCount={items.length}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={rowRenderer}
                        width={width}
                    />
                )}
            </TwinAutoSizer>
        </div>
    )
}

interface CustomSelectDropDownCheckboxRowProps extends DropDownCheckboxRowLogicProps {
    className?: string
    items: TwinDictionary
    keyName?: string
    sortFunction?: (items: TwinDictionary) => ItemCustomSelectDropDownRow[]
}

export const CustomSelectDropDownCheckboxRow: React.FC<CustomSelectDropDownCheckboxRowProps> = ({ className, selectedValues, onChange, ...rest }) => {
    const { text, value, nameComplete, checked, myOnChange } = useDropDownCheckboxRowLogic({ selectedValues, onChange, ...rest })
    return (
        <div className={'custom_select_value_checkbox cursor-pointer flex items-center bg-white regular12 text-gray-51 px-10 gap-x-10 ' + (className || '')} onClick={myOnChange} {...rest} {...value} title={text as any}>
            <InputCheckbox checked={checked} className='ml-5' />
            <span className='block twin_elipsis'>{nameComplete}</span>
        </div>
    )
}

interface DropDownCheckboxRowLogicProps {
    item: any
    selectedValues: any[]
    onChange?: (values: any[]) => void
}

export const useDropDownCheckboxRowLogic = ({ item, selectedValues, onChange }: DropDownCheckboxRowLogicProps) => {
    const value = item.value
    const text = item.text
    const hiddenText = item?.hiddenText
    const nameComplete = item.text + (hiddenText ? (' - ' + hiddenText) : '')

    const checked = selectedValues && selectedValues?.includes(value)

    const myOnChange = useCallback(async (e: any) => {
        e.stopPropagation()
        await onChange?.(value)
    }, [onChange, value])

    return { text, value: { selectValue: value }, nameComplete, checked, myOnChange }
}